/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.14.0
 */

import type { SagaIterator } from 'redux-saga';
import { all, call, put, takeEvery } from 'redux-saga/effects';

import { requestSaga } from '@/main/sagas/requestSaga';
import type { IInputSearchItem } from '@components/common/InputSearch/types';
import { stringFormatParams } from '@utils/stringFormat';

import {
  API_FORM_ADDRESS_SEARCH,
  API_FORM_ADDRESS_SEARCH_PARAM,
  API_FORM_ADDRESS_STRING,
} from './constant/formAddressApi';
import {
  FORM_ADDRESS_GET_AS_STRING_START,
  FORM_ADDRESS_SEARCH_START,
} from './constant/formAddressConstants';
import {
  formAddressGetAsStringFailureAction,
  formAddressGetAsStringSuccessAction,
  formAddressSearchFailureAction,
  formAddressSearchSuccessAction,
} from './formAddressActions';
import {
  formAddressGetAsStringMapping,
  formAddressSearchMapping,
  IFormAddressGetAsStringResponse,
} from './formAddressMapping';
import type {
  IFormAddressGetAsStringSuccessPayload,
  TFormAddressGetAsStringStartAction,
  TFormAddressSearchStartAction,
} from './types/formAddressActionTypes';
import type { IFormAddressSearchResponse } from './types/formAddressTypes';

/*
 * -------------------------------------------
 *       Search for Address Matches
 * -------------------------------------------
 */

export function* formAddressSearchSaga({
  payload,
}: TFormAddressSearchStartAction): SagaIterator {
  try {
    const url = stringFormatParams(API_FORM_ADDRESS_SEARCH, {
      [API_FORM_ADDRESS_SEARCH_PARAM]: payload.type,
    });
    const response: IFormAddressSearchResponse = yield call(
      requestSaga,
      url,
      'POST',
      payload.address,
    );
    const searchItems: IInputSearchItem[] = formAddressSearchMapping(response);

    yield put(
      formAddressSearchSuccessAction({
        searchItems: { [payload.type]: searchItems },
      }),
    );
  } catch (e: any) {
    yield put(formAddressSearchFailureAction(e));
  }
}

/*
 * -------------------------------------------
 *       Get Address as String
 * -------------------------------------------
 */

export function* formAddressGetAsStringSaga({
  payload,
}: TFormAddressGetAsStringStartAction): SagaIterator {
  try {
    const response: IFormAddressGetAsStringResponse = yield call(
      requestSaga,
      API_FORM_ADDRESS_STRING,
      'POST',
      payload.address,
    );
    const addressString: IFormAddressGetAsStringSuccessPayload =
      formAddressGetAsStringMapping(response);
    yield put(formAddressGetAsStringSuccessAction(addressString));
  } catch (e: any) {
    yield put(formAddressGetAsStringFailureAction(e));
  }
}

/*
 * -------------------------------------------
 *       Form Address Sagas
 * -------------------------------------------
 */

export function* formAddressSaga(): SagaIterator {
  yield all([
    takeEvery(FORM_ADDRESS_SEARCH_START, formAddressSearchSaga),
    takeEvery(FORM_ADDRESS_GET_AS_STRING_START, formAddressGetAsStringSaga),
  ]);
}
